import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, User, Phone, MapPin, Trophy, Mail, Save } from "lucide-react";

const COUNTRY_CODES = [
  { value: "MX", code: "+52", label: "MX +52" },
  { value: "US", code: "+1", label: "US +1" },
  { value: "CA", code: "+1", label: "CA +1" },
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("MX");
  const [phone, setPhone] = useState("");
  const [club, setClub] = useState("");
  const [handicapIndex, setHandicapIndex] = useState("");

  const fetchProfile = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/profile`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        setProfile(data);
        setName(data.name || "");
        setPhone(data.phone || "");
        setPhoneCountry(
          COUNTRY_CODES.find(c => c.code === data.phone_country_code)?.value || "MX"
        );
        setClub(data.club || "");
        setHandicapIndex(data.handicap_index !== null && data.handicap_index !== undefined ? String(data.handicap_index) : "");
      }
    } catch {
      toast.error("Error al cargar perfil");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { name: name.trim() || undefined };
      if (phone.trim()) {
        payload.phone = phone.trim();
        const cc = COUNTRY_CODES.find(c => c.value === phoneCountry);
        payload.phone_country_code = cc?.code || "+52";
      }
      if (club.trim()) payload.club = club.trim();
      if (handicapIndex !== "") payload.handicap_index = parseFloat(handicapIndex);

      const resp = await fetch(`${API}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        const updated = await resp.json();
        setProfile(updated);
        setUser(prev => ({ ...prev, name: updated.name, profile_completed: true }));
        toast.success("Perfil actualizado");
      } else {
        throw new Error();
      }
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-10 h-10 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="profile-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold text-[#0F172A]">Mi Perfil</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Avatar & Email */}
        <div className="flex items-center gap-4 mb-8">
          {profile?.picture ? (
            <img src={profile.picture} alt={name} className="w-16 h-16 rounded-full ring-2 ring-[#1B4D3E]/20" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-[#1B4D3E]/10 flex items-center justify-center">
              <User className="w-8 h-8 text-[#1B4D3E]" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-bold text-[#0F172A]">{name || "Sin nombre"}</h2>
            <p className="text-sm text-[#64748B] flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" /> {profile?.email}
            </p>
          </div>
        </div>

        <Card className="card-default" data-testid="profile-form-card">
          <CardContent className="p-6 space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
                <User className="w-4 h-4 text-[#1B4D3E]" /> Nombre
              </Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Tu nombre"
                data-testid="profile-name-input"
              />
              <p className="text-xs text-[#64748B]">Este nombre se usará para identificarte en las rondas</p>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#1B4D3E]" /> Teléfono
              </Label>
              <div className="flex gap-2">
                <Select value={phoneCountry} onValueChange={setPhoneCountry}>
                  <SelectTrigger className="w-[120px]" data-testid="profile-phone-code">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="10 dígitos"
                  maxLength={10}
                  className="flex-1"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>

            {/* Club */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#1B4D3E]" /> Club
              </Label>
              <Input
                value={club}
                onChange={e => setClub(e.target.value)}
                placeholder="Ej: Guadalajara Country Club"
                data-testid="profile-club-input"
              />
            </div>

            {/* INDEX */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#1B4D3E]" /> INDEX
              </Label>
              <Input
                type="number"
                step="0.1"
                value={handicapIndex}
                onChange={e => setHandicapIndex(e.target.value)}
                placeholder="Ej: 12.5"
                data-testid="profile-index-input"
              />
              <p className="text-xs text-[#64748B]">Tu índice de handicap actual (informativo, se usará para historial futuro)</p>
            </div>

            <Button
              className="w-full bg-[#1B4D3E] hover:bg-[#163F33] text-white"
              onClick={handleSave}
              disabled={saving}
              data-testid="profile-save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
